import { create } from 'zustand';
import type { Camera, CameraStatus, CameraSource } from '@asi/shared';
import { supabase } from '@/services/supabase';
import { syncHikDevices } from '@/services/cameras';

export interface AccountCameraOption {
  id: string;
  name: string;
  account_number: string;
  type: 'residential' | 'commercial' | 'enterprise';
  is_active: boolean;
  camera_count: number;
}

interface CamerasState {
  cameras: Camera[];
  selectedCamera: Camera | null;
  loading: boolean;
  syncing: boolean;
  filterStatus: CameraStatus | 'ALL';
  filterSource: CameraSource | 'ALL';
  groupByNvr: boolean;
  search: string;

  // Account-scoped browsing
  accountsForCameras: AccountCameraOption[];
  accountsLoading: boolean;
  selectedAccountId: string | null; // null = show picker, 'ALL' = show all

  fetchAccountsForCameras: () => Promise<void>;
  setSelectedAccount: (accountId: string | null) => void;
  fetchCameras: (silent?: boolean) => Promise<void>;
  syncFromHikvision: () => Promise<{ synced: number }>;
  selectCamera: (camera: Camera | null) => void;
  setFilterStatus: (status: CameraStatus | 'ALL') => void;
  setFilterSource: (source: CameraSource | 'ALL') => void;
  setGroupByNvr: (group: boolean) => void;
  setSearch: (search: string) => void;
  subscribeToCameras: () => () => void;

  // Derived
  filteredCameras: () => Camera[];
  groupedCameras: () => Map<string, Camera[]>;
  statusCounts: () => Record<string, number>;
  sourceCounts: () => Record<string, number>;
  nvrCount: () => number;
}

export const useCamerasStore = create<CamerasState>((set, get) => ({
  cameras: [],
  selectedCamera: null,
  loading: false,
  syncing: false,
  filterStatus: 'ALL',
  filterSource: 'ALL',
  groupByNvr: false,
  search: '',

  accountsForCameras: [],
  accountsLoading: false,
  selectedAccountId: null,

  fetchAccountsForCameras: async () => {
    // Stale-while-revalidate: only show spinner on first load
    if (get().accountsForCameras.length === 0) set({ accountsLoading: true });
    try {
      const { data: accountsData } = await supabase
        .from('accounts')
        .select('id, name, account_number, type, is_active')
        .eq('is_active', true)
        .order('account_number');

      // Count cameras per account via alarm_sites → cameras
      const { data: sitesData } = await supabase
        .from('alarm_sites')
        .select('id, account_id')
        .not('account_id', 'is', null);

      const { data: camerasData } = await supabase
        .from('cameras')
        .select('site_id')
        .eq('is_disabled', false)
        .not('site_id', 'is', null);

      const siteAccountMap = new Map<string, string>();
      (sitesData ?? []).forEach((s: { id: string; account_id: string }) => {
        siteAccountMap.set(s.id, s.account_id);
      });

      const cameraCountMap = new Map<string, number>();
      (camerasData ?? []).forEach((c: { site_id: string }) => {
        const accountId = siteAccountMap.get(c.site_id);
        if (accountId) {
          cameraCountMap.set(accountId, (cameraCountMap.get(accountId) ?? 0) + 1);
        }
      });

      const result: AccountCameraOption[] = (accountsData ?? []).map((a: {
        id: string; name: string; account_number: string; type: string; is_active: boolean;
      }) => ({
        id: a.id,
        name: a.name,
        account_number: a.account_number,
        type: a.type as AccountCameraOption['type'],
        is_active: a.is_active,
        camera_count: cameraCountMap.get(a.id) ?? 0,
      }));

      set({ accountsForCameras: result });
    } finally {
      set({ accountsLoading: false });
    }
  },

  setSelectedAccount: (accountId) => {
    set({ selectedAccountId: accountId, cameras: [], selectedCamera: null });
    if (accountId !== null) {
      setTimeout(() => get().fetchCameras(), 0);
    }
  },

  fetchCameras: async (silent = false) => {
    const { selectedAccountId } = get();
    if (selectedAccountId === null) {
      set({ cameras: [], loading: false });
      return;
    }

    if (!silent) set({ loading: true });
    try {
      if (selectedAccountId === 'ALL') {
        // Load all cameras (admin view)
        const { data } = await supabase
          .from('cameras')
          .select('*')
          .order('name');
        set({ cameras: (data as Camera[] | null) ?? [] });
      } else {
        // Load cameras for this account via alarm_sites
        const { data: sites } = await supabase
          .from('alarm_sites')
          .select('id')
          .eq('account_id', selectedAccountId);

        const siteIds = (sites ?? []).map((s: { id: string }) => s.id);
        if (siteIds.length === 0) {
          set({ cameras: [] });
        } else {
          const { data } = await supabase
            .from('cameras')
            .select('*')
            .in('site_id', siteIds)
            .order('name');
          set({ cameras: (data as Camera[] | null) ?? [] });
        }
      }
    } finally {
      if (!silent) set({ loading: false });
    }
  },

  syncFromHikvision: async () => {
    set({ syncing: true });
    try {
      const result = await syncHikDevices();
      // Refresh cameras list after sync
      await get().fetchCameras();
      return { synced: result.synced };
    } finally {
      set({ syncing: false });
    }
  },

  selectCamera: (camera) => set({ selectedCamera: camera }),

  setFilterStatus: (filterStatus) => set({ filterStatus }),

  setFilterSource: (filterSource) => set({ filterSource }),

  setGroupByNvr: (groupByNvr) => set({ groupByNvr }),

  setSearch: (search) => set({ search }),

  subscribeToCameras: () => {
    const channel = supabase
      .channel(`cameras-realtime-${Date.now()}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'cameras' },
        () => {
          get().fetchCameras(true);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },

  filteredCameras: () => {
    const { cameras, filterStatus, filterSource, search } = get();
    return cameras.filter((c) => {
      if (c.is_disabled) return false;
      if (filterStatus !== 'ALL' && c.status !== filterStatus) return false;
      if (filterSource !== 'ALL' && c.source !== filterSource) return false;
      if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  },

  groupedCameras: () => {
    const cameras = get().filteredCameras();
    const groups = new Map<string, Camera[]>();
    
    // Group standalone cameras under "standalone"
    // Group NVR channels by their nvr_serial
    for (const cam of cameras) {
      if (cam.device_type === 'nvr_channel' && cam.nvr_serial) {
        const existing = groups.get(cam.nvr_serial) ?? [];
        existing.push(cam);
        groups.set(cam.nvr_serial, existing);
      } else {
        const existing = groups.get('standalone') ?? [];
        existing.push(cam);
        groups.set('standalone', existing);
      }
    }
    
    return groups;
  },

  statusCounts: () => {
    const { cameras, filterSource } = get();
    return cameras
      .filter((c) => !c.is_disabled && (filterSource === 'ALL' || c.source === filterSource))
      .reduce<Record<string, number>>((acc, c) => {
        acc[c.status] = (acc[c.status] ?? 0) + 1;
        return acc;
      }, {});
  },

  sourceCounts: () => {
    const { cameras } = get();
    return cameras.filter((c) => !c.is_disabled).reduce<Record<string, number>>((acc, c) => {
      if (c.source) {
        acc[c.source] = (acc[c.source] ?? 0) + 1;
      }
      return acc;
    }, {});
  },

  nvrCount: () => {
    const { cameras, filterSource } = get();
    const nvrSerials = new Set<string>();
    for (const c of cameras) {
      if (c.is_disabled) continue;
      if (filterSource !== 'ALL' && c.source !== filterSource) continue;
      if (c.device_type === 'nvr_channel' && c.nvr_serial) {
        nvrSerials.add(c.nvr_serial);
      }
    }
    return nvrSerials.size;
  },
}));
